import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ChevronsUpDown, UserCog } from "lucide-react";
import { MultiSelect } from "@/components/MultiSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import {
  fetchDepartments,
  fetchGrades,
  fetchLocations,
  updateUserAttributes,
  updateUserPermissions,
  type AdminUser,
} from "@/services/adminApi";
import { ACCESS_LABELS, ALL_ACCESS, applyAllAccessExclusive } from "@/lib/adminConstants";

const USERS_QUERY_KEY = ["admin", "users"] as const;

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");

const parseList = (value: string): string[] =>
  value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

interface EditUserTabProps {
  users: AdminUser[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  selectedId: string;
  onSelectedIdChange: (id: string) => void;
}

export function EditUserTab({
  users,
  isLoading,
  isError,
  onRetry,
  selectedId,
  onSelectedIdChange,
}: EditUserTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });

  const setSelectedId = onSelectedIdChange;
  const [pickerOpen, setPickerOpen] = useState(false);
  const selected = useMemo(
    () => users.find((u) => u.id === selectedId) ?? null,
    [users, selectedId],
  );

  const [deptPerm, setDeptPerm] = useState<string[]>([]);
  const [locationPerm, setLocationPerm] = useState<string[]>([]);
  const [gradePerm, setGradePerm] = useState<string[]>([]);

  const { data: departments = [] } = useQuery({
    queryKey: ["admin", "departments"],
    queryFn: fetchDepartments,
  });
  const { data: locations = [] } = useQuery({
    queryKey: ["admin", "locations"],
    queryFn: fetchLocations,
  });
  const { data: grades = [] } = useQuery({
    queryKey: ["admin", "grades"],
    queryFn: fetchGrades,
  });
  const deptOptions = useMemo(() => [ALL_ACCESS, ...departments], [departments]);
  const locationOptions = useMemo(() => [ALL_ACCESS, ...locations], [locations]);
  const gradeOptions = useMemo(() => [ALL_ACCESS, ...grades], [grades]);

  const [empDept, setEmpDept] = useState<string>("");
  const [empOffice, setEmpOffice] = useState<string>("");
  const [empGrade, setEmpGrade] = useState<string>("");
  const [isEmployee, setIsEmployee] = useState(false);

  useEffect(() => {
    if (!selected) return;
    setDeptPerm(selected.emp_dept_permission);
    setLocationPerm(selected.emp_office_location_permission);
    setGradePerm(selected.emp_grade_permission);
    setEmpDept(selected.emp_dept.join(", "));
    setEmpOffice(selected.emp_office.join(", "));
    setEmpGrade(selected.emp_grade ?? "");
    setIsEmployee(selected.is_employee);
  }, [selected]);

  const permMutation = useMutation({
    mutationFn: () =>
      updateUserPermissions(selectedId, {
        emp_dept_permission: deptPerm,
        emp_office_location_permission: locationPerm,
        emp_grade_permission: gradePerm,
      }),
    onSuccess: () => {
      toast({ title: "Permissions saved" });
      invalidate();
    },
    onError: (e: Error) =>
      toast({ variant: "destructive", title: "Couldn’t save permissions", description: e.message }),
  });

  const attrMutation = useMutation({
    mutationFn: () =>
      updateUserAttributes(selectedId, {
        emp_dept: parseList(empDept),
        emp_office: parseList(empOffice),
        emp_grade: empGrade.trim() || null,
        is_employee: isEmployee,
      }),
    onSuccess: () => {
      toast({ title: "Details saved" });
      invalidate();
    },
    onError: (e: Error) =>
      toast({ variant: "destructive", title: "Couldn’t save details", description: e.message }),
  });

  const permsDirty =
    !!selected &&
    (!sameSet(deptPerm, selected.emp_dept_permission) ||
      !sameSet(locationPerm, selected.emp_office_location_permission) ||
      !sameSet(gradePerm, selected.emp_grade_permission));

  const attrsDirty =
    !!selected &&
    (!sameSet(parseList(empDept), selected.emp_dept) ||
      !sameSet(parseList(empOffice), selected.emp_office) ||
      (empGrade.trim() || null) !== (selected.emp_grade ?? null) ||
      isEmployee !== selected.is_employee);

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-destructive/30 bg-destructive/5 py-16 text-center">
        <AlertCircle className="mb-3 h-6 w-6 text-destructive" aria-hidden />
        <p className="text-sm text-muted-foreground">Couldn’t load users.</p>
        <Button variant="outline" onClick={onRetry} className="mt-4">
          Try again
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return <Skeleton className="h-10 w-full max-w-sm" />;
  }

  return (
    <div className="space-y-6">
      <div className="max-w-sm space-y-1.5">
        <Label htmlFor="edit-user-picker">User</Label>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen} modal>
          <PopoverTrigger asChild>
            <Button
              id="edit-user-picker"
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={pickerOpen}
              className="h-auto min-h-10 w-full justify-between px-3 py-2 font-normal hover:bg-muted hover:text-foreground"
            >
              {selected ? (
                <span className="flex min-w-0 flex-col items-start text-left">
                  <span className="truncate font-medium text-foreground">{selected.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{selected.email}</span>
                </span>
              ) : (
                <span className="text-muted-foreground">Choose a user to edit</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search users…" />
              <CommandList>
                <CommandEmpty>No users found.</CommandEmpty>
                <CommandGroup>
                  {users.map((u) => (
                    <CommandItem
                      key={u.id}
                      value={`${u.name} ${u.email}`}
                      onSelect={() => {
                        setSelectedId(u.id);
                        setPickerOpen(false);
                      }}
                    >
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate font-medium text-foreground">{u.name}</span>
                        <span className="truncate text-xs text-muted-foreground">{u.email}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {!selected ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
          <div className="mb-4 rounded-full bg-secondary p-3">
            <UserCog className="h-6 w-6 text-secondary-foreground" aria-hidden />
          </div>
          <p className="text-sm text-muted-foreground">
            Pick a user above to edit their access and details.
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          <section className="rounded-lg border border-border p-5 space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Data access</h2>
              <p className="text-xs text-muted-foreground">
                Which departments, locations, and grades this user can view. For each, choose "All
                access" or specific values — leaving one empty means no access.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="perm-dept">Departments</Label>
              <MultiSelect
                id="perm-dept"
                options={deptOptions}
                selected={deptPerm}
                onChange={(next) => setDeptPerm(applyAllAccessExclusive(next, deptPerm))}
                labels={ACCESS_LABELS}
                placeholder="No access"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="perm-location">Locations</Label>
              <MultiSelect
                id="perm-location"
                options={locationOptions}
                selected={locationPerm}
                onChange={(next) => setLocationPerm(applyAllAccessExclusive(next, locationPerm))}
                labels={ACCESS_LABELS}
                placeholder="No access"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="perm-grade">Grades</Label>
              <MultiSelect
                id="perm-grade"
                options={gradeOptions}
                selected={gradePerm}
                onChange={(next) => setGradePerm(applyAllAccessExclusive(next, gradePerm))}
                labels={ACCESS_LABELS}
                placeholder="No access"
              />
            </div>

            <Button
              onClick={() => permMutation.mutate()}
              disabled={!permsDirty || permMutation.isPending}
            >
              {permMutation.isPending ? "Saving…" : "Save access"}
            </Button>
          </section>

          <section className="rounded-lg border border-border p-5 space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Employee details</h2>
              <p className="text-xs text-muted-foreground">
                This user’s own department, office, and grade. Comma-separated for more than one.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="attr-dept">Department</Label>
              <Input
                id="attr-dept"
                value={empDept}
                onChange={(e) => setEmpDept(e.target.value)}
                placeholder="e.g. Audit/Accounts, Taxation"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="attr-office">Office</Label>
              <Input
                id="attr-office"
                value={empOffice}
                onChange={(e) => setEmpOffice(e.target.value)}
                placeholder="e.g. Ipswich, Colchester"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="attr-grade">Grade</Label>
              <Input
                id="attr-grade"
                value={empGrade}
                onChange={(e) => setEmpGrade(e.target.value)}
                placeholder="e.g. Manager"
              />
            </div>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div>
                <Label htmlFor="attr-employee" className="cursor-pointer">
                  Is an employee
                </Label>
                <p className="text-xs text-muted-foreground">
                  Marks this account as a firm employee.
                </p>
              </div>
              <Switch id="attr-employee" checked={isEmployee} onCheckedChange={setIsEmployee} />
            </div>

            <Button
              onClick={() => attrMutation.mutate()}
              disabled={!attrsDirty || attrMutation.isPending}
            >
              {attrMutation.isPending ? "Saving…" : "Save details"}
            </Button>
          </section>
        </div>
      )}
    </div>
  );
}
